import { Injectable, computed, effect, signal, untracked } from '@angular/core';
import { filter, skip, take } from 'rxjs';
import { ControlService } from '../../library/control.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { RoleplayFooterState } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayRoleplayService } from './pathway-roleplay.service';
import { CoachLine } from 'src/app/interfaces/icoach-line';

@Injectable()
export class PathwayRoleplayFooterService {
  private readonly control = signal<ControlService | undefined>(undefined);
  private readonly store = signal<CallStoreService | undefined>(undefined);
  private deviceSettings?: DeviceSettingsService;

  get isCameraAvailable(): boolean {
    return !(this.deviceSettings?.isCameraUnavailable ?? true);
  }

  init(params: {
    controlService?: ControlService;
    callStore?: CallStoreService;
    deviceSettingsService?: DeviceSettingsService;
    defaultCaptionsOn?: boolean;
  }): void {
    const { controlService, callStore, defaultCaptionsOn = false } = params;
    this.control.set(controlService);
    this.store.set(callStore);
    this.deviceSettings = params.deviceSettingsService;

    if (controlService) {
      controlService.isCaptionsOn = defaultCaptionsOn;
    }
    this.captionsOn.set(controlService?.isCaptionsOn ?? defaultCaptionsOn);
    this.isVideoOn.set(controlService?.isVideoOn ?? false);
  }

  readonly state = computed<RoleplayFooterState>(() =>
    this.roleplay.footerState(),
  );
  readonly isPreamble = computed(
    () => this.state() === RoleplayFooterState.Preamble,
  );
  readonly isPreambleActive = computed(
    () => this.state() === RoleplayFooterState.PreambleActive,
  );
  /** True while in the launch phase: Preamble or PreambleActive before the main call starts */
  readonly isInLaunchPhase = computed(
    () =>
      this.isPreamble() ||
      (this.isPreambleActive() && this.roleplay.isPreamble()),
  );
  /** True once the main roleplay call is running */
  readonly isActive = computed(() => this.isEnd());
  readonly isEnd = computed(() => this.state() === RoleplayFooterState.End);
  readonly isDebrief = computed(
    () => this.state() === RoleplayFooterState.Debrief,
  );
  readonly isMoveToNext = computed(
    () => this.state() === RoleplayFooterState.MoveToNext,
  );
  readonly isRetryRoleplay = computed(
    () => this.state() === RoleplayFooterState.RetryRoleplay,
  );
  readonly shouldShowVideoButton = computed(
    () =>
      this.isActive() ||
      this.isMoveToNext() ||
      ((this.isRetryRoleplay() || this.isExitPathway()) && !!this.roleplay.debriefInstanceUuid()),
  );
  readonly isExitPathway = computed(
    () => this.state() === RoleplayFooterState.ExitPathway,
  );
  readonly showMediaButtons = computed(
    () =>
      this.isPreamble() ||
      this.isPreambleActive() ||
      this.isEnd() ||
      ((this.isMoveToNext() ||
        this.isExitPathway() ||
        this.isRetryRoleplay()) &&
        !!this.roleplay.debriefInstanceUuid()),
  );
  readonly isHidden = computed(
    () => this.roleplay.isPostCallVisible(),
  );

  readonly actionKind = computed<'primary' | 'destructive'>(() =>
    this.isActive() ? 'destructive' : 'primary',
  );

  readonly actionLabel = computed(() => {
    if (this.roleplay.isResolvingNextStep()) return 'call.PROCESSING';
    if (this.isEnd() || this.isActive()) return 'call.END';
    if (this.isDebrief()) return 'call.START_DEBRIEF';
    if (this.isMoveToNext()) return 'call.MOVE_TO_NEXT';
    if (this.isRetryRoleplay()) return 'call.RETRY_ROLEPLAY';
    if (this.isExitPathway()) return 'call.EXIT_PATHWAY';
    return 'call.LAUNCH_SIMULATION'; // Preamble and PreambleActive (launch phase)
  });

  readonly isActionDisabled = computed(() =>
    this.roleplay.isPrimaryActionDisabled(),
  );
  readonly captionsOn = signal<boolean>(false);
  readonly coachLine = signal<CoachLine | null>(null);

  setCoachLine(coachLine: CoachLine): void {
    this.coachLine.set(coachLine);
  }

  readonly isMicOn = computed(() => this.store()?.getIsMicOn() ?? false);
  readonly isVideoOn = signal<boolean>(false);

  syncVideoState(): void {
    this.isVideoOn.set(this.control()?.isVideoOn ?? false);
  }

  constructor(private readonly roleplay: PathwayRoleplayService) {
    // controlService.isVideoOn is a plain boolean mutated by resetRecognitionControls()
    // (called inside initializeFromStartConversationResponse). footerState transitions to
    // End before the async call setup completes, so isVideoOn must be re-synced when
    // loading finishes. mainIsLoading going false while in End state is the reliable signal.
    effect(() => {
      const loadingDone = !this.roleplay.mainIsLoading();
      if (loadingDone) {
        untracked(() => this.isVideoOn.set(this.control()?.isVideoOn ?? false));
      }
    });
  }

  onCaptionsClick(): void {
    this.control()?.toggleCaptions();
    this.captionsOn.set(this.control()?.isCaptionsOn ?? false);
  }

  onMicrophoneClick(): void {
    this.control()?.toggleMic();
  }

  onVideoClick(): void {
    if (!this.isCameraAvailable) {
      const wasPaused = this.store()?.getIsPaused() ?? false;
      this.deviceSettings?.displayModal$
        .pipe(
          skip(1),
          filter((open) => !open),
          take(1),
        )
        .subscribe(() => {
          if (!wasPaused) {
            this.control()?.unpauseCall();
          }
        });
      this.deviceSettings?.displayDeviceSettingsModal();
      return;
    }
    this.control()?.toggleVideo();
    this.isVideoOn.set(this.control()?.isVideoOn ?? false);
  }
}
